import { type FC, useCallback, useEffect, useState } from 'react';
import { authStore$ } from '../../stores/auth.store';
import { Button, CountdownInput, Fragment, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import { formatTime } from '@kidslabs/module-core';
import { useTranslation } from 'next-i18next';
import { observer } from '@legendapp/state/react';
import type { OtpElapsedTimeCountProps } from './otp-elapsed-time-count.types';
import styles from './otp-elapsed-time-count.module.css';

export const OtpElapsedTimeCount: FC<OtpElapsedTimeCountProps> = observer(({ time, onTimeout, onResend, isLocked }) => {
    const { t } = useTranslation('auth');
    const startTime = authStore$.otpStartTime.get();

    const [timeLeft, setTimeLeft] = useState<number>(time);

    const onResendOtp = useCallback(() => {
        if (onResend && typeof onResend === 'function') {
            onResend();
        }
    }, [onResend]);

    useEffect(() => {
        const currentTime = Math.floor(Date.now() / 1000);
        const elapsedTime = currentTime - (startTime ?? currentTime);
        const remainingTime = time - elapsedTime;

        if (remainingTime > 0) {
            setTimeLeft(remainingTime);
        } else {
            setTimeLeft(0);
        }

        let timerId: number | NodeJS.Timeout | undefined;
        if (timeLeft > 0) {
            timerId = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
        }

        if (timeLeft === 0) {
            onTimeout?.();
        }

        return () => {
            if (timerId) {
                clearTimeout(timerId);
            }
        };
    }, [onTimeout, startTime, time, timeLeft]);

    // Show resend button only when timer expired AND not locked
    const showResendButton = timeLeft === 0 && !isLocked;

    return (
        <Fragment showIf={timeLeft !== undefined}>
            {timeLeft !== undefined && timeLeft > 0 && <CountdownInput time={formatTime(timeLeft)} />}
            <Button showIf={showResendButton} variant="text" className={styles.actionButton} onClick={onResendOtp}>
                <Text className={styles.buttonText}>{t('text_resend_otp')}</Text>
            </Button>
        </Fragment>
    );
});
